interface ErrorReport {
  message: string
  stack?: string
  url?: string
  userAgent?: string
  errorType: 'client' | 'server' | 'api'
  metadata?: Record<string, unknown>
}

// Simple in-memory deduplication
const sentErrors = new Set<string>()
const ERROR_CACHE_TTL = 60000 // 1 minute

// Clean up old entries
setInterval(() => {
  sentErrors.clear()
}, ERROR_CACHE_TTL)

/**
 * Check if an error should be ignored (false positives)
 */
function shouldIgnoreError(errorMessage: string, url?: string): boolean {
  // Ignore OAuth redirect errors - these are expected when Next.js tries to prefetch OAuth URLs
  if (errorMessage.includes('Failed to fetch') && url) {
    // Check if URL contains OAuth-related paths
    if (url.includes('/api/auth/') || url.includes('/auth/') || url.includes('oauth') || url.includes('connect')) {
      return true
    }
    // Check for RSC payload errors on OAuth routes
    if (errorMessage.includes('RSC payload') && (url.includes('/api/auth/') || url.includes('/auth/'))) {
      return true
    }
  }

  // Ignore React Suspense-related errors (common during development)
  if (errorMessage.includes('No group found for id')) {
    return true
  }

  // Ignore hydration errors in development (we're already aware)
  if (errorMessage.includes('Hydration') || errorMessage.includes('hydration')) {
    return true
  }

  return false
}

export async function reportError(error: Error | string, metadata?: Record<string, unknown>) {
  try {
    const errorMessage = typeof error === 'string' ? error : error.message
    const errorStack = typeof error === 'string' ? undefined : error.stack
    const url = typeof window !== 'undefined' ? window.location.href : undefined

    // Skip errors that should be ignored
    if (shouldIgnoreError(errorMessage, url)) {
      return
    }

    // Create fingerprint for deduplication
    const fingerprint = `${errorMessage}-${errorStack?.split('\n')[0] || ''}`

    // Skip if we've already sent this error recently
    if (sentErrors.has(fingerprint)) {
      return
    }

    sentErrors.add(fingerprint)

    const report: ErrorReport = {
      message: errorMessage,
      stack: errorStack,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      errorType: 'client',
      metadata: {
        ...metadata,
        ...(typeof window !== 'undefined' && {
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          screen: {
            width: window.screen.width,
            height: window.screen.height
          }
        })
      }
    }

    // Send to API endpoint (fire and forget)
    fetch('/api/errors/report', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(report),
      // Don't wait for response
      keepalive: true
    }).catch(err => {
      // Silently fail - don't create error loops
      console.warn('Failed to report error:', err)
    })

  } catch (err) {
    // Silently fail - don't create error loops
    console.warn('Error in error reporting:', err)
  }
}
